import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import { FilterList, InfoOutlined, Sort } from '@material-ui/icons'

import React from 'react'
import { TagReviewEntityType, TagReviewItem } from './types'
import { appBarHeight } from '../../App'

export type TagReviewSortField = 'popularity' | 'entityCreatedAt' | 'queuedAt'
export type MessageCompositionFilter = NonNullable<TagReviewItem['messageComposition']>

export const ALL_ENTITY_TYPES: TagReviewEntityType[] = [
  'MessageSetMessage',
  'MarketplaceListing',
  'Pick',
  'Template'
]

export const ALL_COMPOSITIONS: MessageCompositionFilter[] = ['TextOnly', 'Hybrid', 'ArtOnly', 'Unknown']

const entityTypeLabels: Record<TagReviewEntityType, string> = {
  MessageSetMessage: 'Message Set Message',
  MarketplaceListing: 'Marketplace Listing',
  Pick: 'Pick',
  Template: 'Template'
}

const compositionLabels: Record<MessageCompositionFilter, string> = {
  TextOnly: 'Text Only',
  Hybrid: 'Hybrid',
  ArtOnly: 'Art Only',
  Unknown: 'Unknown'
}

const sortFieldLabels: Record<TagReviewSortField, string> = {
  popularity: 'Popularity',
  entityCreatedAt: 'Entity Created',
  queuedAt: 'Queued At'
}

interface TagReviewHeaderProps {
  title?: string
  description?: string | null
  filterEntityTypes: TagReviewEntityType[]
  onFilterEntityTypesChange: (types: TagReviewEntityType[]) => void
  filterCompositions: MessageCompositionFilter[]
  onFilterCompositionsChange: (compositions: MessageCompositionFilter[]) => void
  sortField: TagReviewSortField
  sortDir: 'asc' | 'desc'
  onSortChange: (field: TagReviewSortField, dir: 'asc' | 'desc') => void
}

const useStyles = makeStyles(theme => ({
  header: {
    position: 'sticky',
    top: appBarHeight,
    zIndex: theme.zIndex.appBar - 1,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3)
  },
  headerContent: {
    maxWidth: 960,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  iconButton: {
    padding: theme.spacing(0.75)
  },
  popoverPaper: {
    padding: theme.spacing(2),
    minWidth: 230
  },
  popoverTitle: {
    fontWeight: 700,
    display: 'block',
    marginBottom: theme.spacing(0.5)
  },
  dirRow: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center'
  },
  activeIcon: {
    color: theme.palette.primary.main
  }
}))

export const TagReviewHeader: React.FC<TagReviewHeaderProps> = ({
  title = 'Tag Review',
  description,
  filterEntityTypes,
  onFilterEntityTypesChange,
  filterCompositions,
  onFilterCompositionsChange,
  sortField,
  sortDir,
  onSortChange
}) => {
  const classes = useStyles()
  const [filterAnchor, setFilterAnchor] = React.useState<HTMLElement | null>(null)
  const [sortAnchor, setSortAnchor] = React.useState<HTMLElement | null>(null)

  const isFiltered = filterEntityTypes.length < ALL_ENTITY_TYPES.length || filterCompositions.length < ALL_COMPOSITIONS.length
  const isSorted = sortField !== 'popularity' || sortDir !== 'desc'

  const handleTypeToggle = (type: TagReviewEntityType) => {
    const allSelected = filterEntityTypes.length === ALL_ENTITY_TYPES.length
    if (allSelected) {
      onFilterEntityTypesChange([type])
      return
    }
    const next = filterEntityTypes.includes(type)
      ? filterEntityTypes.filter(t => t !== type)
      : [...filterEntityTypes, type]
    if (next.length > 0) onFilterEntityTypesChange(next)
  }

  const handleCompositionToggle = (composition: MessageCompositionFilter) => {
    const allSelected = filterCompositions.length === ALL_COMPOSITIONS.length
    if (allSelected) {
      onFilterCompositionsChange([composition])
      return
    }
    const next = filterCompositions.includes(composition)
      ? filterCompositions.filter(c => c !== composition)
      : [...filterCompositions, composition]
    if (next.length > 0) onFilterCompositionsChange(next)
  }

  return (
    <Box className={classes.header}>
      <Box className={classes.headerContent}>
        <Box display='flex' flexDirection='column' style={{ flex: 1 }}>
          <Typography variant='h5'>{title}</Typography>
          {description && <Typography variant='body2' color='textSecondary' style={{ marginTop: 4 }}>{description}</Typography>}
        </Box>
        <Box display='flex' alignItems='center' style={{ gap: 4 }}>
          <Tooltip title='Filter by entity type'>
            <IconButton className={classes.iconButton} onClick={e => setFilterAnchor(e.currentTarget)}>
              <FilterList className={isFiltered ? classes.activeIcon : undefined} fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Sort items'>
            <IconButton className={classes.iconButton} onClick={e => setSortAnchor(e.currentTarget)}>
              <Sort className={isSorted ? classes.activeIcon : undefined} fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.popoverPaper }}>
        <Typography variant='caption' className={classes.popoverTitle}>
          Entity Types
        </Typography>
        <FormGroup>
          {ALL_ENTITY_TYPES.map(type => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  size='small'
                  checked={filterEntityTypes.includes(type)}
                  onChange={() => handleTypeToggle(type)}
                  color='primary'
                />
              }
              label={<Typography variant='body2'>{entityTypeLabels[type]}</Typography>}
            />
          ))}
        </FormGroup>
        <Typography variant='caption' className={classes.popoverTitle} style={{ marginTop: 12, display: 'block' }}>
          Message Composition
        </Typography>
        <FormGroup>
          {ALL_COMPOSITIONS.map(composition => (
            <FormControlLabel
              key={composition}
              control={
                <Checkbox
                  size='small'
                  checked={filterCompositions.includes(composition)}
                  onChange={() => handleCompositionToggle(composition)}
                  color='primary'
                />
              }
              label={<Typography variant='body2'>{compositionLabels[composition]}</Typography>}
            />
          ))}
        </FormGroup>
      </Popover>

      {/* Sort Popover */}
      <Popover
        open={Boolean(sortAnchor)}
        anchorEl={sortAnchor}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.popoverPaper }}>
        <Typography variant='caption' className={classes.popoverTitle}>
          Sort By
        </Typography>
        <RadioGroup value={sortField} onChange={e => onSortChange(e.target.value as TagReviewSortField, sortDir)}>
          {(['popularity', 'entityCreatedAt', 'queuedAt'] as TagReviewSortField[]).map(field => (
            <FormControlLabel
              key={field}
              value={field}
              control={<Radio size='small' color='primary' />}
              label={
                <Box display='flex' alignItems='center' style={{ gap: 4 }}>
                  <Typography variant='body2'>{sortFieldLabels[field]}</Typography>
                  {field === 'popularity' && (
                    <Tooltip
                      title='Installs for marketplace listings; saves/favorites for picks, templates, and message set messages. Higher = more popular.'
                      arrow>
                      <InfoOutlined style={{ fontSize: 14, opacity: 0.5 }} />
                    </Tooltip>
                  )}
                </Box>
              }
            />
          ))}
        </RadioGroup>
        <Box className={classes.dirRow}>
          <Typography variant='caption' color='textSecondary' style={{ marginRight: 8 }}>
            Direction:
          </Typography>
          <RadioGroup row value={sortDir} onChange={e => onSortChange(sortField, e.target.value as 'asc' | 'desc')}>
            <FormControlLabel
              value='desc'
              control={<Radio size='small' color='primary' />}
              label={<Typography variant='body2'>Desc</Typography>}
            />
            <FormControlLabel
              value='asc'
              control={<Radio size='small' color='primary' />}
              label={<Typography variant='body2'>Asc</Typography>}
            />
          </RadioGroup>
        </Box>
      </Popover>
    </Box>
  )
}
